import { RefObject, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { routes } from 'app/routing/routesConfig';
import { useAppSelector, useAppDispatch } from 'app/store';
import {
  addPlaylistAudiosEffect,
  createPlaylistEffect,
} from 'app/store/Playlists/effects';
import {
  getCreatePlaylistAudioTemplatedLink,
  getPlaylistsList,
} from 'app/store/Playlists/selectors';
import {
  getAllSubscriptionQuotas,
  getSubscription,
} from 'app/store/Subscription/selectors';
import { SubscriptionQuotaItem } from 'types/subscription.types';
import { SelectedAudioObject } from 'app/components/Audio';
import { useFetchValidationSchema } from 'app/utils/hooks';

const MAX_AUDIO_COUNT_IN_LIST = 100;

export const usePlaylistHeaderOpacity = (
  scrollableContainerRef: RefObject<HTMLDivElement>,
  thresholdScrollTop: number,
  scrollFromThresholdToFullFade: number
) => {
  const [opacity, setOpacity] = useState(1);
  const listenScrollEvent = useCallback(() => {
    if (scrollableContainerRef.current) {
      const scrollTop = scrollableContainerRef.current.scrollTop || 0;
      if (scrollTop > thresholdScrollTop) {
        const value =
          1 - (scrollTop - thresholdScrollTop) / scrollFromThresholdToFullFade;
        setOpacity(value <= 0 ? 0 : value);
      } else {
        setOpacity(1);
      }
    }
  }, [
    scrollFromThresholdToFullFade,
    scrollableContainerRef,
    thresholdScrollTop,
  ]);

  useEffect(() => {
    const scrollableEl = scrollableContainerRef.current;
    if (scrollableEl) {
      scrollableEl.addEventListener('scroll', listenScrollEvent);
    }
    return () => {
      if (scrollableEl) {
        scrollableEl.removeEventListener('scroll', listenScrollEvent);
      }
    };
  }, [listenScrollEvent, scrollableContainerRef]);

  return opacity;
};

export const useCreatePlaylistLimit = () => {
  const playlists = useAppSelector(getPlaylistsList);
  const subscription = useAppSelector(getSubscription);
  const quotas = useAppSelector(getAllSubscriptionQuotas);
  const [limit, setLimit] = useState<SubscriptionQuotaItem>();

  useEffect(() => {
    if (quotas) {
      if (subscription?.hasSubscription) {
        setLimit(quotas[subscription?.tier || '']);
      } else {
        setLimit(quotas.default);
      }
    }
  }, [quotas, subscription]);

  const isLimitReached = Boolean(
    !subscription?.hasSubscription &&
      limit?.playlistLimit &&
      playlists.length >= limit?.playlistLimit
  );

  return { isLimitReached };
};

export const useCreatePlaylist = (
  songGuid?: string,
  callbackOnError?: () => void
) => {
  const addAudioLink = useAppSelector(getCreatePlaylistAudioTemplatedLink);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { validationSchema } = useFetchValidationSchema({
    link: addAudioLink,
  });
  const maxPlaylistAudios =
    validationSchema?.properties?.playlistAudios?.maxItems ||
    MAX_AUDIO_COUNT_IN_LIST;

  const handleOnAdd = async (audios: SelectedAudioObject) => {
    try {
      const { guid } = await dispatch(
        createPlaylistEffect({
          audioCount: Object.keys(audios).length,
          songGuid,
        })
      ).unwrap();
      navigate(routes.playlist.path.replace(':guid', guid), {
        state: { shouldNotFetch: true },
      });
      await dispatch(
        addPlaylistAudiosEffect({
          audios,
          playlistGuid: guid,
          songGuid,
          withPlaylistCreate: true,
        })
      ).unwrap();
    } catch (e: any) {
      if (e.code === 403) {
        navigate(routes.playlists.path, {
          state: { shouldShowPopup: true },
          replace: true,
        });
        if (callbackOnError) {
          callbackOnError();
        }
      }
      // proceed
    }
  };
  return { maxPlaylistAudios, handleOnAdd };
};
